import { AuthUser, getCurrentUser, signOut } from '@aws-amplify/auth';
import { LoadingPage, Maintenance, NotFound } from '@chiroup/components';
import { classNames, hexToRgb } from '@chiroup/core';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Route, Routes, useNavigate } from 'react-router-dom';
import useLocation from '../hooks/useLocation';
import userService from '../services/user.service';
import Login from './Login';
import { NotFoundSchedule } from './NotFoundSchedule';
import NotSetup from './NotSetup';
import Schedule from './Schedule';
import ThankYou from './ThankYou';
import Appointments from './appointments/Appointments';

export type ScheduleForm = {
  treatment: number | null;
  provider: string | null;
  date: string | null;
  startTime: string | null;
  endTime: string | null;
  time: string | null;
  patient: string | null;
  fname?: string | null;
  lname?: string | null;
  email?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
};

const primaryColorDefault = '#93cc68';
const defaultValues: ScheduleForm = {
  treatment: null,
  provider: null,
  date: null,
  startTime: null,
  endTime: null,
  time: null,
  patient: null,
  fname: null,
  lname: null,
  email: null,
  phone: null,
  dateOfBirth: null,
};

const Container = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [downForMaintenance, setDownForMaintenance] = useState<{
    title: string;
    message: string;
  } | null>(null);
  const navigate = useNavigate();
  const { data, path, isFetching } = useLocation();
  const [me, setMe] = useState<{
    patients: any[];
  } | null>(null);
  const [values, setValues] = useState<ScheduleForm>(defaultValues);

  useEffect(() => {
    const root = document.documentElement;
    if (data?.primaryColor) {
      root.style.setProperty(
        '--primary-color-base',
        hexToRgb(data.primaryColor),
      );
      root.style.setProperty(
        '--primary-color',
        `rgba(${hexToRgb(data.primaryColor)})`,
      );
    }
    if (data?.accentColor) {
      root.style.setProperty('--accent-color', hexToRgb(data.accentColor));
    }
  }, [data]);

  const getMe = useMemo(() => {
    return async () => {
      const meRes = await userService.me(path).catch((err: any) => {
        if (err?.response?.data?.statusCode === 503) {
          const root = document.documentElement;
          root.style.setProperty('--primary-color-base', hexToRgb('#000000'));
          root.style.setProperty(
            '--primary-color',
            `rgba(${hexToRgb('#000000')})`,
          );
          setDownForMaintenance(err?.response?.data);
        }
      });
      setMe(meRes);
      return meRes;
    };
  }, [path]);

  useEffect(() => {
    let isDestroyed = false;
    const doIt = async () => {
      try {
        const user = await getCurrentUser();
        if (!isDestroyed && user) {
          setUser(user as any);
          getMe();
        }
      } catch (err) {
        await signOut();
        setUser(null);
        setMe(null);
        setValues(defaultValues);
      }
    };
    if (path) {
      doIt();
    }
    return () => {
      isDestroyed = true;
    };
  }, [path, getMe]);

  const logout = async () => {
    await signOut();
    setUser(null);
    setMe(null);
    setValues(defaultValues);
    navigate(`/${path}`);
  };

  if (downForMaintenance) {
    return (
      <Maintenance
        title={downForMaintenance.title}
        message={downForMaintenance.message}
      />
    );
  }

  if (isFetching) {
    return <LoadingPage />;
  }

  if (!data) {
    return <NotFound />;
  }

  if (!data.disciplines?.length) {
    return <NotSetup />;
  }

  return path ? (
    <div className="bg-white min-h-screen">
      <header className="absolute inset-x-0 top-0 z-50">
        <nav
          className="flex items-center justify-between p-6 lg:px-8"
          aria-label="Global"
        >
          <div className="flex lg:flex-1">
            <Link to={`/${path}`} className="-m-1.5 p-1.5">
              <span className="sr-only">{data?.name}</span>
              <img className="h-16 w-auto" src={data?.logo} alt={data?.name} />
            </Link>
          </div>
          {me ? (
            <Menu as="div" className="relative inline-block text-left">
              <div>
                <Menu.Button className="flex items-center text-gray-700 hover:text-gray-900">
                  <span className="sr-only">Open options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 h-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.982 18.725A7.488 7.488 0 0012 15.75a7.488 7.488 0 00-5.982 2.975m11.963 0a9 9 0 10-11.963 0m11.963 0A8.966 8.966 0 0112 21a8.966 8.966 0 01-5.982-2.275M15 9.75a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  {/* <Bars3Icon className="h-6 w-6" aria-hidden="true" /> */}
                </Menu.Button>
              </div>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <div className="py-1">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to={`/${path}`}
                          className={classNames(
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700',
                            'block px-4 py-2 text-sm',
                          )}
                        >
                          New Appointment
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to={`/${path}/appointments`}
                          className={classNames(
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700',
                            'block px-4 py-2 text-sm',
                          )}
                        >
                          My Appointments
                        </Link>
                      )}
                    </Menu.Item>
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={logout}
                          className={classNames(
                            active
                              ? 'bg-gray-100 text-gray-900'
                              : 'text-gray-700',
                            'block w-full px-4 py-2 text-left text-sm',
                          )}
                        >
                          Sign Out
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Transition>
            </Menu>
          ) : (
            <div className="justify-end">
              <NavLink
                to={`/${path}/login`}
                className={({ isActive }) =>
                  (isActive ? 'invisible ' : '') +
                  'lg:flex lg:flex-1 text-sm font-semibold leading-6 text-gray-900 cursor-pointer'
                }
                end
              >
                Log in <span aria-hidden="true">&rarr;</span>
              </NavLink>
            </div>
          )}
        </nav>
      </header>
      <main className="isolate">
        <div className="relative pt-14">
          <div
            className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
            aria-hidden="true"
          >
            <div
              className={`relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]`}
              style={
                {
                  clipPath:
                    'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
                  '--tw-gradient-to': data?.primaryColor,
                  '--tw-gradient-from': data?.accentColor,
                  '--tw-gradient-stops':
                    'var(--tw-gradient-from), var(--tw-gradient-to)',
                } as any
              }
            />
          </div>
          <div className="py-6 sm:py-24">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
              <Routes>
                <Route
                  path="/"
                  element={
                    <Schedule
                      data={data}
                      path={path}
                      user={user}
                      setUser={setUser}
                      getMe={getMe}
                      me={me}
                      values={values}
                      setValues={setValues}
                    />
                  }
                />
                <Route
                  path="/login"
                  element={
                    <Login
                      setUser={setUser}
                      path={path}
                      getMe={getMe}
                      values={values}
                      setValues={setValues}
                    />
                  }
                />
                <Route path="/thank-you" element={<ThankYou path={path} />} />
                <Route
                  path="/appointments"
                  element={
                    <Appointments
                      path={path}
                      location={data}
                      me={me}
                      primaryColor={data?.primaryColor || primaryColorDefault}
                    />
                  }
                />
                <Route path="*" element={<NotFoundSchedule />} />
              </Routes>
            </div>
          </div>
        </div>
      </main>
    </div>
  ) : (
    <NotFound />
  );
};

export default Container;
